import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';
import { WizardProgress, WizardSection } from '../wizard/WizardComponents';
import { CourseTable, LanguageTable } from './CourseTable';

const RegistrationStep2 = ({ onNavigate, onLogout }) => {
  const [courses, setCourses] = useState([
    { universityName: '', academicYear: '', stream: '', timing: '', phoneNo: '' },
    { universityName: '', academicYear: '', stream: '', timing: '', phoneNo: '' },
    { universityName: '', academicYear: '', stream: '', timing: '', phoneNo: '' }
  ]);

  const [languages, setLanguages] = useState({
    toWrite: '',
    toSpeak: '',
    toRead: '',
    motherTongue: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showValidationSummary, setShowValidationSummary] = useState(false);

  // Load data from localStorage if exists
  useEffect(() => {
    const savedData = localStorage.getItem('registrationStep2Data');
    if (savedData) {
      const { courses: savedCourses, languages: savedLanguages } = JSON.parse(savedData);
      if (savedCourses) setCourses(savedCourses);
      if (savedLanguages) setLanguages(savedLanguages);
    }
  }, []);

  const validateCourse = (index, field, value) => {
    if (!value || value.trim() === '') {
      return 'This field is required';
    }
    
    if (field === 'phoneNo') {
      if (!/^\d{10,15}$/.test(value)) {
        return 'Phone number must be between 10-15 digits';
      }
    }
    
    if (field === 'academicYear') {
      if (!/^\d{4}-\d{2,4}$/.test(value)) {
        return 'Format should be YYYY-YY (e.g., 2015-16)';
      }
    }

    // universityName and stream must have each word starting with capital
    if (field === 'universityName' || field === 'stream') {
      const words = value.trim().split(/\s+/);
      for (const word of words) {
        if (word.length > 0 && word[0] !== word[0].toUpperCase()) {
          return 'Each word must start with a capital letter';
        }
      }
    }
    
    return '';
  };

  const validateLanguage = (field, value) => {
    if (!value || value.trim() === '') {
      return 'This field is required';
    }
    if (value.trim().length < 2) {
      return 'Please enter at least 2 characters';
    }
    // Each word (split by space or comma) must start with a capital letter
    const words = value.trim().split(/[\s,]+/).filter(Boolean);
    for (const word of words) {
      if (word.length > 0 && word[0] !== word[0].toUpperCase()) {
        return 'Each language must start with a capital letter (e.g. English, Hindi)';
      }
    }
    return '';
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    
    // Clear validation summary when user starts correcting
    setShowValidationSummary(false);
    
    // Validate course fields
    if (field.startsWith('course_')) {
      const [, indexStr, fieldName] = field.split('_');
      const index = parseInt(indexStr);
      const value = courses[index][fieldName];
      const error = validateCourse(index, fieldName, value);
      setErrors({ ...errors, [field]: error });
    }
    
    // Validate language fields
    else if (['toWrite', 'toSpeak', 'toRead', 'motherTongue'].includes(field)) {
      const error = validateLanguage(field, languages[field]);
      setErrors({ ...errors, [field]: error });
    }
  };

  const handleSubmit = () => {
    const newErrors = {};
    const newTouched = {};
    let hasError = false;

    // Validate at least one course is filled
    const hasAtLeastOneCourse = courses.some(course => 
      course.universityName || course.academicYear || course.stream || 
      course.timing || course.phoneNo
    );

    if (!hasAtLeastOneCourse) {
      newErrors['general_course'] = 'Please fill at least one course entry';
      hasError = true;
    }

    // Validate all filled courses
    courses.forEach((course, index) => {
      const isCourseStarted = course.universityName || course.academicYear || 
                              course.stream || course.timing || course.phoneNo;
      
      if (isCourseStarted) {
        ['universityName', 'academicYear', 'stream', 'timing', 'phoneNo'].forEach(field => {
          const fieldKey = `course_${index}_${field}`;
          newTouched[fieldKey] = true;
          const error = validateCourse(index, field, course[field]);
          if (error) {
            newErrors[fieldKey] = error;
            hasError = true;
          }
        });
      }
    });

    // Validate all language fields
    ['toWrite', 'toSpeak', 'toRead', 'motherTongue'].forEach(field => {
      newTouched[field] = true;
      const error = validateLanguage(field, languages[field]);
      if (error) {
        newErrors[field] = error;
        hasError = true;
      }
    });

    setErrors(newErrors);
    setTouched(newTouched);
    setShowValidationSummary(hasError);

    if (hasError) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Save to localStorage
    localStorage.setItem('registrationStep2Data', JSON.stringify({ courses, languages }));
    onNavigate('registration3');
  };

  const handleSaveDraft = () => {
    localStorage.setItem('registrationStep2Data', JSON.stringify({ courses, languages }));
    alert('Draft saved successfully!');
  };

  // Get error list for validation summary
  const getErrorList = () => {
    const errorList = [];
    
    // Course errors
    Object.keys(errors).forEach(key => {
      if (key.startsWith('course_') && errors[key]) {
        const [, index, field] = key.split('_');
        const fieldName = field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1');
        errorList.push(`Course ${parseInt(index) + 1} - ${fieldName}: ${errors[key]}`);
      }
    });
    
    // Language errors
    if (errors.toWrite) errorList.push(`Languages (Write): ${errors.toWrite}`);
    if (errors.toSpeak) errorList.push(`Languages (Speak): ${errors.toSpeak}`);
    if (errors.toRead) errorList.push(`Languages (Read): ${errors.toRead}`);
    if (errors.motherTongue) errorList.push(`Mother Tongue: ${errors.motherTongue}`);
    
    // General errors
    if (errors.general_course) errorList.push(errors.general_course);
    
    return errorList;
  };

  return (
    <div className="app-layout">
      <Sidebar activeItem="registration" onNavigate={onNavigate} onLogout={onLogout} />
      
      <main className="main-content">
        <div className="top-bar">
          <h1 className="page-title">Student Registration</h1>
        </div>
        
        <div className="content-area">
          <div className="wizard-container">
            <WizardProgress currentStep={2} />
            
            <div className="wizard-card">
              {showValidationSummary && getErrorList().length > 0 && (
                <div className="validation-summary">
                  <div className="validation-summary-title">⚠️ Please fix the following errors:</div>
                  <ul className="validation-summary-list">
                    {getErrorList().map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <WizardSection 
                icon="🎓" 
                title="Academic Information" 
                subtitle="University and course details (Fill at least one course)"
              >
                {errors.general_course && (
                  <div style={{
                    padding: '12px',
                    marginBottom: '20px',
                    backgroundColor: '#fee',
                    border: '1px solid #e74c3c',
                    borderRadius: '8px',
                    color: '#e74c3c',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    {errors.general_course}
                  </div>
                )}
                <CourseTable 
                  courses={courses}
                  setCourses={setCourses}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                />
              </WizardSection>
              
              <WizardSection 
                icon="🗣️" 
                title="Language Proficiency" 
                subtitle="Communication skills and mother tongue"
              >
                <LanguageTable 
                  languages={languages}
                  setLanguages={setLanguages}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                />
              </WizardSection>
              
              <div className="wizard-actions">
                <button className="btn-back" onClick={() => onNavigate('registration1', { isBack: true })}>Back</button>
                <div className="btn-group">
                  <button className="btn-secondary" onClick={handleSaveDraft}>Save as Draft</button>
                  <button className="btn-next" onClick={handleSubmit}>
                    Save & Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegistrationStep2;